/**
 * Company Mapper
 * Maps between API DTOs and Domain Entities
 */

import { CompanyDTO, CreateCompanyResponseDTO, ListCompaniesResponseDTO } from "../models/CompanyDTOs";
import { CompanyEntity } from "../../domain/entities/CompanyEntity";

export class CompanyMapper {
  /**
   * Convert CompanyDTO to CompanyEntity
   */
  static toCompanyEntity(dto: CompanyDTO): CompanyEntity {
    // Handle both boolean and number for boolean fields
    const isActive = typeof dto.is_active === 'boolean' 
      ? dto.is_active 
      : dto.is_active === 1;
    
    const msme = typeof dto.msme === 'boolean'
      ? dto.msme
      : dto.msme === 1;
    
    const companyGstEnabled = typeof dto.company_gst_enabled === 'boolean'
      ? dto.company_gst_enabled
      : dto.company_gst_enabled === 1;

    return {
      id: dto.id!,
      company_name: dto.company_name,
      app_url: dto.app_url,
      address_line1: dto.address_line1,
      address_line2: dto.address_line2,
      state: dto.state,
      country: dto.country,
      pincode: dto.pincode,
      gst_number: dto.gst_number,
      pan_number: dto.pan_number,
      cin_number: dto.cin_number,
      currency: dto.currency,
      msme: msme,
      zoho_org_id: dto.zoho_org_id,
      zoho_client_id: dto.zoho_client_id,
      zoho_client_secret: dto.zoho_client_secret,
      zoho_redirect_uri: dto.zoho_redirect_uri,
      cashfree_client_id: dto.cashfree_client_id,
      cashfree_client_secret: dto.cashfree_client_secret,
      gstin_verification_required: dto.gstin_verification_required,
      dso_lookback_months: dto.dso_lookback_months,
      accounting_start_month: dto.accounting_start_month,
      amount_compact_unit: dto.amount_compact_unit,
      amount_compact_decimals: dto.amount_compact_decimals,
      company_gst_enabled: companyGstEnabled,
      vendor_type: dto.vendor_type,
      is_active: isActive,
      created_at: dto.created_at,
      updated_at: dto.updated_at,
    };
  }

  /**
   * Convert CompanyDTO array to CompanyEntity array
   */
  static toCompanyEntityArray(dtos: CompanyDTO[]): CompanyEntity[] {
    return dtos.map((dto) => this.toCompanyEntity(dto));
  }

  /**
   * Convert CreateCompanyResponseDTO to domain result
   */
  static toCreateCompanyResult(dto: CreateCompanyResponseDTO): {
    company: CompanyEntity;
    admin_credentials: { email: string; password: string };
  } {
    return {
      company: this.toCompanyEntity(dto.data.company),
      admin_credentials: dto.data.admin_credentials,
    };
  }

  /**
   * Convert ListCompaniesResponseDTO to domain result
   */
  static toListCompaniesResult(dto: ListCompaniesResponseDTO): {
    companies: CompanyEntity[];
    pagination: {
      current_page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  } {
    return {
      companies: this.toCompanyEntityArray(dto.data.companies),
      pagination: dto.data.pagination,
    };
  }
}

